/*
 * Keeps the session's `free_event` current while a free event is active.
 * Rails computes its fields when the session is requested (texts sent, the
 * cap, the window ending, the limited dashboard), so a dashboard left open
 * while the booth runs would otherwise keep its first answer.
 *
 * Reuses the app's one session path (fetchSession → the App saga's
 * takeLatest), so the banner, the limited shell and every other reader of
 * the user see the same refresh. Kept cheap: every minute while the event is
 * live (texts move fast), every five minutes otherwise, plus once when the
 * tab comes back into view; never while hidden or offline, and not at all
 * once there is nothing left to change (paid, gone, unused, or no event).
 */
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { fetchSession } from "containers/App/actions";
import { bannerState } from "./state";

export const LIVE_MS = 60 * 1000;
export const IDLE_MS = 5 * 60 * 1000;
export const FOCUS_GAP_MS = 15 * 1000;

// How often to refresh for this payload; null means don't.
export const refreshEvery = (fe, now = new Date()) => {
  const s = bannerState(fe, now);
  if (!s || s === "gone" || s === "unused") return null;
  return fe.state === "live" ? LIVE_MS : IDLE_MS;
};

const looking = () =>
  (typeof document === "undefined" || document.visibilityState !== "hidden") &&
  (typeof navigator === "undefined" || navigator.onLine !== false);

const useFreeEventRefresh = (fe) => {
  const dispatch = useDispatch();
  const every = refreshEvery(fe);
  const last = useRef(Date.now());

  useEffect(() => {
    if (!every) return undefined;
    const refresh = () => {
      if (!looking()) return;
      last.current = Date.now();
      dispatch(fetchSession());
    };
    const onBack = () => {
      if (Date.now() - last.current >= FOCUS_GAP_MS) refresh();
    };
    const id = setInterval(refresh, every);
    window.addEventListener("focus", onBack);
    document.addEventListener("visibilitychange", onBack);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onBack);
      document.removeEventListener("visibilitychange", onBack);
    };
  }, [every, dispatch]);
};

export default useFreeEventRefresh;
